'use strict';
import chunk from 'lodash/chunk';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isDate from 'lodash/isDate';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import toLower from 'lodash/toLower';
import values from 'lodash/values';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import {serverValForSortMethod, SortMethod, SyncStatus} from './enums';


const hasOwnProperty = Object.prototype.hasOwnProperty;

export const BASE_OPTIONS = {
  pageSize: 20,
  syncPageSize: 500,
  initialState: {},
  syncNotifierThrottle: 5000,
  syncFilterDebounce: 5000,
  syncModelAttemptThrottle: 1000,
  crossDomain: false,
  hasParentId: true,
  useAutoFilter: false,
  useFilterOptions: false,
  useSortOptions: false,
  useDelete: false,
  useSyncUpdates: false,
  canSyncCreate: false,
  serializeFilterOptions,
  serializeSortOptions,
  listToArray,
  parseListResult,
  parseList: defaultParseList,
  parseListSyncResult,

  isRecoverable,
  getSyncModelBody,

  cacheOptions: null,
};

export const BASE_CACHE_OPTIONS = {
  throttle: 1000,
};

/**
 * @param {ReduxObservableListOptions} options
 */
export function validateOptions(options) {
  if (options.useFilterOptions) {
    if (!isObject(options.defaultFilterOptions))
      throw new Error('ReduxListEpics missing required defaultFilterOptions');
  }

  if (options.useSortOptions) {
    if (!isObject(options.defaultSortOptions))
      throw new Error('ReduxListEpics missing required defaultSortOptions');
    if (!isFunction(options.applySortOptions))
      throw new Error('ReduxListEpics missing required applySortOptions');
  }

  if (options.useFiltering || options.useFilterOptions) {
    if (!isFunction(options.isFilterDataDefault))
      throw new Error('ReduxListEpics missing required isFilterDataDefault');
  }

  if (!isFunction(options.syncNotifier))
    throw new Error('ReduxListEpics missing required syncNotifier');

  if (options.hasParentId) {
    if (!isFunction(options.listPath))
      throw new Error('ReduxListEpics missing required function listPath');
    if (!isFunction(options.listSyncPath))
      throw new Error('ReduxListEpics missing required function listSyncPath');
  } else {
    if (!isString(options.listPath))
      throw new Error('ReduxListEpics missing required string listPath');
    if (!isString(options.listSyncPath))
      throw new Error('ReduxListEpics missing required string listSyncPath');
  }

  if (!isFunction(options.parseListResult))
    throw new Error('ReduxListEpics missing required parseListResult');

  if (!isFunction(options.parseListSyncResult))
    throw new Error('ReduxListEpics missing required parseListSyncResult');

  if (options.useDelete) {
    if (!isFunction(options.deleteItemPath))
      throw new Error('ReduxListEpics missing required deleteItemPath');
  }

  if (options.useSyncUpdates) {
    if ((!options.hasParentId && !isString(options.syncModelPath))
      || (options.hasParentId && !isFunction(options.syncModelPath)))
      throw new Error('ReduxListEpics missing required syncModelPath');
    if (!isArray(options.syncModelActionsTypes))
      throw new Error('ReduxListEpics missing required syncModelActionsTypes');
  }

  if (options.cacheOptions) {
    if (!isObject(options.cacheOptions.localForage))
      throw new Error('ReduxListEpics missing required localForage instance');

    if (options.hasParentId) {
      if (!isFunction(options.cacheOptions.getKey))
        throw new Error('ReduxListEpics missing required cacheOptions.getKey');

    } else {
      if (!isString(options.cacheOptions.key))
        throw new Error('ReduxListEpics missing required cacheOptions.key');

    }
  }
}

/** @type {ReduxObservableListOptions~serializeFilterOptions} **/
export function serializeFilterOptions(initialState, currentState) {
  const result = serializeFilterOptionsRecurse(initialState, currentState);

  return result !== null && Object.keys(result).length > 0 ? JSON.stringify(result) : null;
}

/** @type {ReduxObservableListOptions~serializeSortOptions} **/
function serializeFilterOptionsRecurse(initialState, currentState) {
  const result = {};

  for (let key in currentState) {
    if (currentState.hasOwnProperty(key)) {
      const initial = initialState[key];
      const current = currentState[key];
      if (current !== initial) {
        if (isDate(current)) {
          result[key] = current.getTime();
        } else if (isObject(current)) {
          if (isObject(initial)) {
            const keyResult = serializeFilterOptionsRecurse(initial, current);
            if (keyResult !== null) {
              result[key] = keyResult;
            }
          } else {
            result[key] = current;
          }
        } else {
          result[key] = current;
        }
      }
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}

/**
 * @param {ReduxObservableListState} initialState
 * @param {ReduxObservableListState} currentState
 * @returns {String}
 */
export function serializeSortOptions(initialState, currentState) {
  const result = {};

  if (initialState.currentMethod !== currentState.currentMethod) {
    result.currentMethod = serverValForSortMethod(currentState.currentMethod);
  }

  if (SortMethod.NONE !== currentState.currentMethod && initialState.property !== currentState.property) {
    result.property = currentState.property;
  }

  return Object.keys(result).length > 0 ? JSON.stringify(result) : null;
}

/**
 * @param {ReduxObservableListOptions} options
 * @param {ReduxObservableListState} state
 * @return {FilterData}
 */
export function getFilterDataFromListState(options, state) {
  const filterData = {};

  if (options.useAutoFilter)
    filterData.filter = state.filter;

  if (options.useFilterOptions)
    filterData.filterOptions = state.filterOptions;

  if (options.useSortOptions)
    filterData.sortOptions = state.sortOptions;

  return filterData;
}

// TODO: improve epic error handler. maybe just resubscribe asynchronously after logging the error?

const MAX_ERROR_COUNT = 100;

export function epicErrorHandler(logger, name) {
  let errorCount = 0;
  /**
   * @param {*} err
   * @param {Observable<T>} source$
   * @return {Observable<T>}
   * @template T
   */
  return (err, source$) => {
    logger.error({err}, name);
    return errorCount < MAX_ERROR_COUNT
      ? Observable.timer(errorCount++ * 2 + 100)
        .mergeMap(() => source$)
      : Observable.empty();
  }
}

/**
 * @param {Logger} logger
 * @param {String} name
 * @param {any|Function} mapTo If mapTo is or results in undefined, no value will be emitted
 * @param {Function} [performRetry=5]
 * @return {function(Error, Observable)}
 */
export function expoBackoff(logger, name, mapTo, performRetry=useMaxAttempts) {
  let errorCount = 0;
  return (err, source$) => {
    logger.error({err}, name);
    return performRetry(err, errorCount)
      ? Observable.timer(++errorCount * 5000)
        .mergeMap(() => source$)
      : getErrorHandlerFallback(mapTo);
  }
}

/**
 * @param {Error} error
 * @param {Number} errorCount
 * @param {Number} [maxAttempts=5]
 * @return {Boolean}
 */
export function useMaxAttempts(error, errorCount, maxAttempts = 10) {
  return errorCount < maxAttempts;
}

function getErrorHandlerFallback(err, mapTo) {
  let result;
  if (isFunction(mapTo)) {
    result = mapTo(err);
  } else {
    result = mapTo;
  }
  return isUndefined(result) ? Observable.empty() : Observable.of(result);
}

/**
 * Curried function to apply an auto-filter to specified properties.
 * Tokenizes the input filter by " " character.
 *
 * @param {Array<String>} autoFilterProperties
 * @param {Number} [threshold=60%] Matching threshold by number of filter tokens.
 * @param {Boolean} [caseSensitive=false]
 */
export function applyAutoFilter(autoFilterProperties, threshold=0.6, caseSensitive=false) {
  /**
   * @param {Object} item
   * @param {String} filter
   * @return {Boolean} Item matches filter
   */
  return function applyAutoFilterCurried(item, filter) {
    const tokens = (caseSensitive ? filter : toLower(filter)).split(' ').filter(Boolean);
    // check for each token
    const matches = tokens.reduce((acc, token) => {
      // check for occurrences of the current token. We only need to find one.
      const found = autoFilterProperties
        .reduce((isFound, property) => isFound || contains(get(item, property), token, caseSensitive), false);
      return acc + (found ? 1 : 0);
    }, 0);

    return threshold > 0.99 ? matches === tokens.length : matches / tokens.length >= threshold;
  };
}

/**
 * TODO: we need to accurately match the behavior of the server search mechanism.
 * @param {String} str
 * @param {String} filter
 * @param {Boolean} caseSensitive
 * @return {Boolean}
 */
function contains(str, filter, caseSensitive) {
  return isString(str) && (caseSensitive ? str : toLower(str)).indexOf(filter) > -1;
}

/**
 * @param {AjaxResponse} ajaxResponse
 * @param {ReduxObservableListOptions~parseList} [parseList=defaultParseList]
 * @return {ListResult}
 */
export function parseListResult(ajaxResponse, parseList=defaultParseList) {
  if (ajaxResponse && ajaxResponse.responseType === 'json') {
    if (isObject(ajaxResponse.response)) {
      if (isArray(ajaxResponse.response.list)) {
        return {
          hasMore: ajaxResponse.response.hasMore,
          list: parseList(ajaxResponse.response.list),
          size: ajaxResponse.response.list.length,
          total: ajaxResponse.response.total,
        };
      }
    }
  }

  // Fallback
  return {
    hasMore: false,
    list: {},
    size: 0,
    total: 0,
  };
}

/**
 * @param {Array<Object>} list
 * @return {Object<String, Object>}
 */
export function defaultParseList(list) {
  return list.reduce(toObject, {});
}

/**
 * @param {AjaxResponse} ajaxResponse
 * @param {ReduxObservableListOptions~parseSyncList} [parseSyncList=defaultParseList]
 * @return {ListSyncResult}
 */
export function parseListSyncResult(ajaxResponse, parseSyncList=defaultParseSyncList) {
  if (ajaxResponse && ajaxResponse.responseType === 'json') {
    if (isObject(ajaxResponse.response)) {
      if (isObject(ajaxResponse.response.list)) {
        return {
          hasMore: ajaxResponse.response.hasMore,
          list: parseSyncList(ajaxResponse.response.list),
          total: ajaxResponse.response.total,
        };
      }
    }
  }

  // Fallback
  return {
    hasMore: false,
    list: {},
    total: 0,
  };
}

function defaultParseSyncList(list) {
  return list;
}

/**
 * Use with Array.prototype.reduce to convert an array to an object.
 * Assumes the presence of an 'id' property.
 *
 * @param {Object} acc
 * @param {Object} record
 * @return {*}
 */
export function toObject(acc, record) {
  acc[record.id] = record;
  return acc;
}

/**
 * Default implementation performs a deep-equals check on each selected datum.
 * @param {ReduxObservableListOptions} options
 */
export function isFilterDataDefault(options) {
  /**
   * @param {FilterData} filterData
   * @return {Boolean}
   */
  return function (filterData) {
    if (options.useAutoFilter && filterData.filter)
      return false;

    if (options.useSortOptions && !isEqual(options.defaultSortOptions, filterData.sortOptions))
      return false;

    if (options.useFilterOptions && !isEqual(options.defaultFilterOptions, filterData.filterOptions))
      return false;

    return true;
  };
}

/**
 * @typedef {Object} ETagData
 * @property {Object} etags
 * @property {Number} size
 */

/**
 * @param {ReduxObservableListOptions} options
 * @param {ReduxObservableListState} state
 * @returns {Array}
 */
export function listToArray(options, state) {
  let list = values(state.list);
  if (state.isFiltered) {
    list = removeInvisible(list);
  }

  return options._hasSort ? options.applySortOptions(list, state.sortOptions) : list;
}

export function removeInvisible(list) {
  return list.filter(isVisibleItem);
}

export function isVisibleItem(item) {
  return isUndefined(item.isVisible) || item.isVisible;
}

/**
 * Extract all pages of etags from state, in order.
 * @param {ReduxObservableListOptions} options
 * @param {ReduxObservableListState} state
 * @returns {Array<ETagData>}
 */
export function getEtagPages(options, state) {
  const list = listToArray(options, state);
  const length = list.length;
  const etagPages = [];

  if (length === 0) {
    return etagPages;
  } else if (length < options.syncPageSize) {
    etagPages.push(extractEtagPage(list, 0, options.syncPageSize).etagData);
    return etagPages;
  } else {
    let i = 0;
    while (i < length) {
      const result = extractEtagPage(list, i, options.syncPageSize);
      etagPages.push(result.etagData);
      i = result.endAt + 1;
    }

    return chunk(list, options.syncPageSize);
  }
}

/**
 * @param {Object} list
 * @param {Number} startAt
 * @param {Number} pageSize
 * @returns {{endAt: Number, etagData: ETagData}}
 */
export function extractEtagPage(list, startAt, pageSize) {
  const etags = {};
  let size = 0;
  let endAt = startAt + 1;

  for (let i = startAt, len = list.length; i < len && size <= pageSize; i++) {
    if (list[i].id && list[i].ETag) {
      etags[list[i].id] = list[i].ETag;
      size++;
    }
    endAt = startAt + i;
  }

  return {
    endAt,
    etagData: {
      etags,
      size,
    },
  }
}

/**
 * Like Object.assign, but will not overwrite with a falsey value.
 * @param {Object} target The target object.
 * @param {Object} source The source object.
 * @returns {Object}
 */
export function mergeSyncListResults(target, source) {
  for (let key in source) {
    if (hasOwnProperty.call(source, key) && !target[key] && source[key]) {
      target[key] = source[key];
    }
  }
  return target;
}

export function nearestPage(listSize, pageSize, syncPageSize) {
  return Math.min(Math.max(Math.ceil(listSize / pageSize) * pageSize, pageSize), syncPageSize);
}

/**
 * Whether a
 * @param {ReduxObservableListState} state
 * @return {Boolean}
 */
export function canRequestPage(state) {
  return state.isFiltered ? state.filteredHasMore : state.hasMore;
}

/**
 * Default implementation for useSyncUpdates=true
 * @type {ReduxObservableListOptions~isProcessing|ReduxObservableItemOptions~isProcessing}
 */
export function isProcessing(item, options, state) {
  return item.syncStatus !== SyncStatus.NONE && item.syncStatus !== SyncStatus.SUCCESS;
}

/**
 * Test if the response status is normally recoverable.
 * @param {Number} status
 * @return {Boolean}
 */
export function isRecoverable(status) {
  return status !== 501 && (status === 0 || (status >= 500 && status < 600));
}

export function getSyncModelBody(model) {
  return model;
}

/**
 * @param {ReduxObservableListOptions} options
 * @param {ListPageRequest} request
 * @return {String}
 */
export function getPageFilterParams(options, request) {
  const params = [];

  // check filter
  if (options.useAutoFilter && request.filterData.filter)
    params.push(`&filter=${encodeURIComponent(request.filterData.filter)}`);

  // check filterOptions
  if (options.useFilterOptions && request.filterData.filterOptions) {
    const filterOptions = options.serializeFilterOptions(options.defaultFilterOptions, request.filterData.filterOptions);
    if (filterOptions !== null)
      params.push(`&filterOptions=${encodeURIComponent(filterOptions)}`);
  }

  // check sortOptions
  if (options.useSortOptions && request.filterData.sortOptions) {
    const sortOptions = options.serializeSortOptions(options.defaultSortOptions, request.filterData.sortOptions);
    if (sortOptions !== null)
      params.push(`&sortOptions=${encodeURIComponent(sortOptions)}`);
  }

  return params.join('');
}



// WEBPACK FOOTER //
// ./src/js/_libs/redux-observable-list/src/utils.js